/** @format */

import {ReactElement} from 'react';

import {format} from 'date-fns/format';
import {ru} from 'date-fns/locale/ru';
import {Trans} from 'react-i18next';

import {differenceInDays} from 'date-fns/differenceInDays';
import {endOfDay} from 'date-fns/endOfDay';
import {isBefore} from 'date-fns/isBefore';
import {startOfDay} from 'date-fns/startOfDay';

import {AccountsKpiName} from 'graphql/generated/types';

import {formatNumber} from 'helpers/formatNumber';

import {FAR_FUTURE} from './constants';
import {CompanyMediaplanFromQuery, KPI, KpiLocalSettings, Period, PeriodType} from './interfaces';

const KPIS_WITH_METRIC_GOALS: AccountsKpiName[] = ['CPL', 'CONVERSIONS'];

export const getDayOfWeekInGenitive = (date: Date): string | ReactElement => {
  const day = format(date, 'eeee', {locale: ru});
  const genitiveMap: Record<string, ReactElement> = {
    понедельник: <Trans>понедельника</Trans>,
    вторник: <Trans>вторника</Trans>,
    среда: <Trans>среды</Trans>,
    четверг: <Trans>четверга</Trans>,
    пятница: <Trans>пятницы</Trans>,
    суббота: <Trans>субботы</Trans>,
    воскресенье: <Trans>воскресенья</Trans>,
  };
  return genitiveMap[day] || day;
};

export const transformKpiToLocalSettings = ({name, primary, value}: KPI): KpiLocalSettings => {
  return {
    id: name,
    name,
    primary,
    value,
    needsMetricGoals: KPIS_WITH_METRIC_GOALS.includes(name),
  };
};

export const transformKpis = ({id, primary, value}: KpiLocalSettings): KPI => {
  return {
    name: id,
    primary: primary || false,
    value: value,
  };
};

export const parseFormattedNumber = (value: string): number => {
  const cleanValue = value.replace(/\s/g, '').replace(',', '.');
  return Math.trunc(parseFloat(cleanValue)) || 0;
};

export const formatBudgetPlan = (value: string) => {
  const cleanValue = value.replace(/[^\d.,]/g, '');
  const number = parseFloat(cleanValue.replace(',', '.'));

  if (isNaN(number)) {
    return {
      formatted: '',
      value: 0,
    };
  }

  const intValue = Math.trunc(number);

  return {
    formatted: formatNumber(intValue),
    value: intValue,
  };
};

export const formatKpiValue = (value: number | null): string => {
  if (value === null) return '';
  return formatNumber(Math.trunc(value));
};

export const getRepeatOn = (repeat: string) => {
  switch (repeat) {
    case 'MONTHLY':
      return 'monthly' as const;
    case 'WEEKLY':
      return 'weekly' as const;
    default:
      return 'monthly' as const;
  }
};

export const getType = (type: string): PeriodType => {
  switch (type) {
    case 'FLIGHT':
      return 'flight';
    case 'REPEATABLE':
      return 'repeatable';
    default:
      return 'repeatable';
  }
};

export const calculateDailyBudget = (period?: Period | null): number | null => {
  if (!period?.budgetPlan || !period.startDate) return null;

  let days = 30;
  if (period.repeatEvery === 'weekly') {
    days = 7;
  } else if (period.type === 'flight' && period.endDate) {
    days = differenceInDays(period.endDate, period.startDate) + 1;
  }

  return Math.round(period.budgetPlan / days);
};

export type BusyInterval = {
  id?: number | string | null;
  start: Date | null;
  end: Date | null;
};

export const isRangeOverlapping = (
  start: Date,
  end: Date,
  intervals: BusyInterval[],
  excludeId?: number | string | null,
  farFuture?: Date,
): boolean => {
  const normalizedStart = startOfDay(start);
  const normalizedEnd = endOfDay(end);
  const openEnd = farFuture || startOfDay(FAR_FUTURE);

  return intervals.some(interval => {
    if (!interval.start) return false;
    if (excludeId != null && interval.id === excludeId) return false;
    const s = startOfDay(interval.start);
    const e = interval.end ? endOfDay(interval.end) : openEnd;
    return !(isBefore(normalizedEnd, s) || isBefore(e, normalizedStart));
  });
};

export const getBusyIntervalsCb = (
  externalMediaplans: ReadonlyArray<CompanyMediaplanFromQuery>,
  periods: Period[],
  excludeId?: string | null,
): BusyInterval[] => {
  const external: BusyInterval[] = externalMediaplans.map(m => ({
    id: m.id,
    start: m.startOn ? new Date(m.startOn) : null,
    end: m.endOn ? new Date(m.endOn) : null,
  }));
  const local: BusyInterval[] = periods
    .filter(p => !(excludeId && p.id === excludeId))
    .map(p => ({
      id: p.id,
      start: p.startDate || null,
      end: p.endDate || null,
    }));
  return [...external, ...local];
};
